import { api } from '@/lib/axios';

export interface ListFilters {
  companyIds?: string[];
  positionIds?: string[];
  tagIds?: string[];
}

export interface List {
  id: string;
  name: string;
  description?: string;
  filters?: ListFilters;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  membersCount: number;
}

export interface ListMember {
  id: string;
  listId: string;
  clientId: string;
  addedType: 'manual' | 'filter';
  createdAt: string;
  client: {
    id: string;
    name: string;
    email?: string;
    phone?: string;
    document?: string;
  };
}

export interface LastMessage {
  id: string;
  content: string;
  sentAt: string;
  status: string;
  flowExecution?: {
    flow?: {
      name: string;
    };
  };
}

export interface ListMemberWithMessage extends ListMember {
  client: ListMember['client'] & {
    lastMessage: LastMessage | null;
  };
}

export interface ListWithMembers extends List {
  members: ListMember[];
}

export interface CreateListDto {
  name: string;
  description?: string;
  filters?: ListFilters;
  active?: boolean;
}

export interface UpdateListDto extends Partial<CreateListDto> {}

export interface ListsResponse {
  data: List[];
  page: number;
  total: number;
  totalPages: number;
}

export interface ApplyFiltersDto {
  filters: ListFilters;
}

export const listsService = {
  async getAll(params?: {
    search?: string;
    page?: number;
    limit?: number;
    activeOnly?: boolean;
  }): Promise<ListsResponse> {
    const { data } = await api.get('/lists', { params });
    return data;
  },

  async getById(id: string): Promise<ListWithMembers> {
    const { data } = await api.get(`/lists/${id}`);
    return data;
  },

  async create(list: CreateListDto): Promise<ListWithMembers> {
    const { data } = await api.post('/lists', list);
    return data;
  },

  async update(id: string, list: UpdateListDto): Promise<ListWithMembers> {
    const { data } = await api.put(`/lists/${id}`, list);
    return data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/lists/${id}`);
  },

  async applyFilters(id: string, filters: ListFilters): Promise<ListWithMembers> {
    const { data } = await api.post(`/lists/${id}/apply-filters`, { filters });
    return data;
  },

  async addMember(id: string, clientId: string): Promise<ListWithMembers> {
    const { data } = await api.post(`/lists/${id}/members`, { clientId });
    return data;
  },

  async addMembers(id: string, clientIds: string[]): Promise<ListWithMembers> {
    const { data } = await api.post(`/lists/${id}/members/bulk`, { clientIds });
    return data;
  },

  async removeMember(id: string, memberId: string): Promise<void> {
    await api.delete(`/lists/${id}/members/${memberId}`);
  },

  async getMembers(id: string): Promise<ListMember[]> {
    const { data } = await api.get(`/lists/${id}/members`);
    return data;
  },

  async getMembersWithMessages(id: string): Promise<ListMemberWithMessage[]> {
    const { data } = await api.get(`/lists/${id}/members-with-messages`);
    return data;
  },
};
